/**
 * Meta / Facebook Pixel — self-registering.
 *
 * PAGE_VIEW → PageView; ECOM_CLICKOUT → Lead (+ CompleteRegistration / Search
 * per yoto ecom parity); SESSION_BID_CENT_INCREMENT → `sv_n_*` custom value
 * events. All fires use `trackSingle*` so multi-pixel setups do not over-fire.
 *
 * Cent milestones are reported as CUSTOM events only. Mapping them onto Meta
 * standard events is deliberately not done: yoto disabled that in 2024 after it
 * was suspected of destabilising Meta's optimisation, and the final session
 * value now reaches Meta server-side as one Purchase (roas-manager
 * `PURCHASE_ROAS` over the `roas-v3` table, RND-10989).
 */

import { debug, warn } from '../../util/log';
import type { FacebookPixel } from '../../core/config/types';
import { collectAttributionIds } from '../../core/analytics/attributionIds';
import { on, SdkEvent, type EcomClickoutSignal } from '../../core/events/bus';
import type { PixelContext, PixelRegister } from './types';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

const SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';
let scriptInjected = false;

function ensureFbq(): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & {
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue: unknown[][];
      push: (...args: unknown[]) => void;
      loaded?: boolean;
      version?: string;
    };
    _fbq?: unknown;
  };

  if (!w.fbq) {
    const stub = function (...args: unknown[]): void {
      const fn = stub as unknown as { callMethod?: (...a: unknown[]) => void; queue: unknown[][] };
      if (fn.callMethod) fn.callMethod.apply(stub, args);
      else fn.queue.push(args);
    } as unknown as Window['fbq'] & {
      callMethod?: (...args: unknown[]) => void;
      queue: unknown[][];
      push: (...args: unknown[]) => void;
      loaded: boolean;
      version: string;
    };
    stub.queue = [];
    stub.push = stub as unknown as (...args: unknown[]) => void;
    stub.loaded = true;
    stub.version = '2.0';
    w.fbq = stub;
    if (!w._fbq) w._fbq = stub;
  }

  if (!scriptInjected) {
    scriptInjected = true;
    try {
      const script = document.createElement('script');
      script.async = true;
      script.src = SCRIPT_URL;
      (document.head || document.documentElement).appendChild(script);
    } catch (err) {
      warn('fb script inject failed', err);
    }
  }
}

function normalizeProductId(productId: string): string {
  if (!productId.startsWith('http')) return productId;
  try {
    const url = new URL(productId);
    return btoa((url.pathname + url.search).substring(0, 100)).replace(/=/g, '').slice(-36);
  } catch {
    return productId.slice(0, 64);
  }
}

function productCustomData(contentId: string, contentCategory: string) {
  return {
    currency: 'USD',
    value: 0.0,
    content_ids: [contentId],
    content_category: contentCategory,
    content_name: contentId,
    contents: [{ id: contentId, quantity: 1, item_price: 0.0 }],
    content_type: 'product',
  };
}

/** yoto `sendFbqCentEvent` customData — value is nominal, the tier is the signal. */
function centCustomData(context: PixelContext, valueInCents: number) {
  const mbid = context.getPageParams().mediaBuyingId || 'mbid';
  const postId = context.getOptions().postId ?? '';
  const contentId = `adv_${mbid}_${valueInCents}`;
  return {
    currency: 'USD',
    value: 0.01,
    content_ids: [contentId],
    content_category: postId ? `post_${postId}` : 'web',
    content_name: `v_${valueInCents}`,
    contents: [{ id: contentId, quantity: 1, item_price: 0.01 }],
    content_type: 'product',
  };
}

export const registerFacebook: PixelRegister<FacebookPixel> = (pixel, context) => {
  let inited = false;

  on(SdkEvent.PAGE_VIEW, () => {
    if (context.getConsent() !== 'granted') {
      debug('fb PageView skipped (consent not granted)', { pixelId: pixel.pixel_id });
      return;
    }
    try {
      ensureFbq();
      const fbq = window.fbq!;
      if (!inited) {
        inited = true;
        fbq('dataProcessingOptions', [], 0, 0);
        fbq('consent', 'grant');
        fbq('init', pixel.pixel_id, { external_id: context.getIdentity().sessionId });
        debug('fb pixel inited', { pixelId: pixel.pixel_id });
      }
      fbq('trackSingle', pixel.pixel_id, 'PageView');
      debug('fb PageView', { pixelId: pixel.pixel_id });
    } catch (err) {
      warn('fb pageview failed', err);
    }
  });

  on(SdkEvent.ECOM_CLICKOUT, (signal: EcomClickoutSignal) => {
    if (context.getConsent() !== 'granted') {
      debug('fb Lead skipped (consent not granted)', { pixelId: pixel.pixel_id });
      return;
    }
    try {
      const fbq = window.fbq;
      if (!fbq) {
        debug('fb Lead skipped (fbq missing)', { pixelId: pixel.pixel_id });
        return;
      }

      const sessionId = context.getIdentity().sessionId;
      const mbid = context.getPageParams().mediaBuyingId || 'mbid';
      const market = signal.ecomProductMarket || '';
      const rawProductId =
        signal.productId !== undefined
          ? String(signal.productId)
          : signal.advertiserCampaignId !== undefined
            ? String(signal.advertiserCampaignId)
            : '';
      const normalizedId = rawProductId ? normalizeProductId(rawProductId) : '';
      const fullProductId = `${market}${normalizedId}`;
      const leadContentId = `adv_${mbid}_${fullProductId}`;
      const uniqContentId = `adv_${mbid}`;

      fbq(
        'trackSingle',
        pixel.pixel_id,
        'Lead',
        productCustomData(leadContentId, 'web_prod_click'),
        { eventID: `${sessionId}Lead${fullProductId}` },
      );
      debug('fb Lead', { pixelId: pixel.pixel_id, contentId: leadContentId });

      if (signal.unique) {
        fbq(
          'trackSingle',
          pixel.pixel_id,
          'CompleteRegistration',
          productCustomData(uniqContentId, 'web_prod_uniq_click'),
          { eventID: `${sessionId}CompleteRegistrationecomuniq` },
        );
        debug('fb CompleteRegistration', { pixelId: pixel.pixel_id });
      }

      if (signal.sessionClicks < 5) {
        fbq(
          'trackSingle',
          pixel.pixel_id,
          'Search',
          productCustomData(uniqContentId, 'web_prod_scc_click'),
          { eventID: `${sessionId}Searchecomscc` },
        );
        debug('fb Search', { pixelId: pixel.pixel_id, sessionClicks: signal.sessionClicks });
      }
    } catch (err) {
      warn('fb clickout failed', err);
    }
  });

  on(SdkEvent.SESSION_BID_CENT_INCREMENT, (cents) => {
    if (context.getConsent() !== 'granted') return;
    if (!context.getConfig().analytics.fbReportEvents) return;
    try {
      const fbq = window.fbq;
      if (!fbq) return;

      if (cents >= 26) return;

      const fbc = collectAttributionIds().fbc ?? '';
      const sessionId = context.getIdentity().sessionId;
      const eventId = (name: string, suffix?: number) =>
        `${sessionId}${fbc}${name}${suffix !== undefined ? `_${suffix}` : ''}`;

      const name = `sv_n_${cents}`;
      fbq('trackSingleCustom', pixel.pixel_id, name, undefined, { eventID: eventId(name) });

      // whole cents only in the aggregate event
      if (cents % 1 === 0) {
        fbq('trackSingleCustom', pixel.pixel_id, 'sv_n_all', centCustomData(context, cents), {
          eventID: eventId('sv_n_all', cents),
        });
      }
      debug('fb cent value', { pixelId: pixel.pixel_id, cents });
    } catch (err) {
      warn('fb cent event failed', err);
    }
  });
};

/** Test-only — clears module state so re-registration works in fresh test cases. */
export function _resetFacebookForTests(): void {
  scriptInjected = false;
}
